'use no memo'

import { useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useSpaceViewPageSetup } from '../hooks/useSpaceViewPageSetup'
import { SpaceTasksContent } from '../components/SpaceTasksContent'
import type { ViewType } from '@/store/ProjectViewPreferencesStore'
import type { FilterFieldKey } from '@/lib/types/filter'
import { useViewHydrator } from '@/routes/features/views/useViewHydrator'
import { ProjectViewControls } from '@/routes/features/projects/components/ProjectViewControls'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Loading } from '@/components/app/indicators/Loading'
import { Button } from '@/components/ui/button'
import { parseSavedLayoutConfig } from '@/lib/types/savedFilter'

const PINNED_FIELDS: Array<FilterFieldKey> = ['project']

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey/views/$viewId',
)({
  component: SpaceViewPage,
})

function SpaceViewPage() {
  const { workspaceSlug, spaceKey, viewId } = Route.useParams()
  const navigate = useNavigate()

  const { space, spaceLoading, topLevelTasks, listTasks, isLoading, density } =
    useSpaceViewPageSetup(workspaceSlug, spaceKey, (spaceId) => (
      <ProjectViewControls
        projectId={spaceId}
        scope="space"
        pinnedFields={PINNED_FIELDS}
      />
    ))

  const { status, view } = useViewHydrator({
    viewId,
    scopeEntityId: space?.id,
    scope: 'space',
    pinnedFields: PINNED_FIELDS,
  })

  const viewTypeOverride = useMemo<ViewType | undefined>(() => {
    const layout = parseSavedLayoutConfig(view?.layoutConfig ?? null)
    return layout?.viewType
  }, [view])

  // Refresh breadcrumb title with the view name while viewing it.
  usePageLayout(
    {
      breadcrumbActions: space ? (
        <ProjectViewControls
          projectId={space.id}
          scope="space"
          pinnedFields={PINNED_FIELDS}
        />
      ) : null,
    },
    [space?.id, view?.id, view?.name],
  )

  if (spaceLoading || status === 'pending') {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!space) return null

  if (status === 'missing' || status === 'forbidden') {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {status === 'forbidden'
            ? 'You do not have access to this view.'
            : 'This view no longer exists.'}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            navigate({
              to: '/$workspaceSlug/spaces/$spaceKey/views',
              params: { workspaceSlug, spaceKey },
            })
          }
        >
          Back to views
        </Button>
      </div>
    )
  }

  return (
    <SpaceTasksContent
      spaceId={space.id}
      tasks={topLevelTasks}
      listTasks={listTasks}
      isLoading={isLoading}
      density={density}
      viewTypeOverride={viewTypeOverride}
      currentView={view}
    />
  )
}
